import { Offer } from 'schema-dts';

import { BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';
import { shouldSeeAnnualBundlePlan } from '~/features/checkout/utils/should-see-annual-plan';
import { Currency } from '~/features/internationalization/models/currency';
import { BraavosPlanPrice, BraavosPlanPrices } from '~/features/plans/models/braavos-plan-prices';
import { BraavosProgramPriceResponse } from '~/features/plans/models/braavos-program-price';

const PATH_TO_MODULE = 'src/features/seo/utils/seo-price-helpers.ts';

// Inlined copy of `getMinorUnitDivisor` from
// `src/features/accreditation/utils/compute-dynamic-price.ts`. We avoid
// importing that module here because it pulls in `~/features/internationalization`
// (the `i18n` singleton) → `@udacity/application`, whose `lodash/merge` ESM
// import does not resolve under Vitest. The SEO helpers must stay side-effect
// free and free of that dependency chain so unit tests can import them.
function getMinorUnitDivisor(currency: Currency): number {
  const fractionDigits =
    new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().minimumFractionDigits ?? 2;
  return 10 ** fractionDigits;
}

// Google requires priceValidUntil to be a FUTURE ISO 8601 date; past dates
// suppress the price rich result. We pin offers to a fixed ~1 year out from
// build time so ISR-revalidated pages always carry a future date without
// needing per-offer expiry data from Braavos.
const PRICE_VALID_UNTIL_DAYS = 365;

const IN_STOCK = 'https://schema.org/InStock';
const NEW_CONDITION = 'https://schema.org/NewCondition';

export const SEO_DEFAULT_CURRENCY: Currency = 'USD';

// Mirrors `PROGRAM_MONTHS` in `src/features/accreditation/utils/compute-dynamic-price.ts`.
// Duplicated here (rather than imported) to keep SEO helpers free of the
// `~/features/internationalization` → `@udacity/application` import chain,
// which does not resolve under Vitest. Kept in sync with the source constant.
// Shared by the accreditation price controller and the structured-data builder
// so the program duration stays in sync between price calculation and JSON-LD.
export const PROGRAM_MONTHS = 18;

/**
 * Returns the number of fraction digits a currency renders with (e.g. 2 for
 * USD, 0 for JPY). Reused so JSON-LD `price` strings match the precision of the
 * visible Braavos display strings.
 */
export function getCurrencyFractionDigits(currency: Currency): number {
  return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().minimumFractionDigits ?? 2;
}

/**
 * Converts a Braavos minor-unit amount (e.g. cents) into the numeric string
 * Google's `Offer.price` requires: no currency symbol, no thousands separators,
 * `.` as the decimal separator (e.g. `24900` -> `"249.00"` for USD).
 */
export function formatOfferPrice(minorUnits: number, currency: Currency): string {
  const divisor = getMinorUnitDivisor(currency);
  const fractionDigits = getCurrencyFractionDigits(currency);
  const majorUnits = minorUnits / divisor;
  return majorUnits.toFixed(fractionDigits);
}

/**
 * ISO 8601 date (YYYY-MM-DD) roughly one year from `now`, used for
 * `Offer.priceValidUntil`. Computed from the supplied reference time so unit
 * tests can pin it deterministically.
 */
export function computePriceValidUntil(now: Date = new Date()): string {
  const result = new Date(now.getTime());
  result.setDate(result.getDate() + PRICE_VALID_UNTIL_DAYS);
  return result.toISOString().slice(0, 10);
}

function buildBaseOffer({
  priceMinorUnits,
  currency,
  url,
  priceValidUntil,
  name,
}: {
  priceMinorUnits: number;
  currency: Currency;
  url: string;
  priceValidUntil: string;
  name?: string;
}): Offer {
  const offer: Offer = {
    '@type': 'Offer',
    price: formatOfferPrice(priceMinorUnits, currency),
    priceCurrency: currency,
    availability: IN_STOCK,
    itemCondition: NEW_CONDITION,
    url,
    priceValidUntil,
  };
  if (name) {
    offer.name = name;
  }
  return offer;
}

/**
 * Shape of the price data the PLP `getStaticProps`/`getServerSideProps` passes
 * into the SEO builders. All amounts are Braavos minor units (cents for USD)
 * and are the **default (pre-discount / no-coupon)** prices. Any field may be
 * absent when the corresponding Braavos call failed or did not return a plan;
 * the SEO builders gracefully omit offers they cannot price.
 *
 * We intentionally use the default (list) prices — not the coupon-discounted
 * `payable` amounts — so the structured-data price reflects the standard
 * program pricing a default logged-out viewer sees, independent of any
 * transient sale or coupon.
 */
export type PlpSeoPriceData = {
  currency: Currency;
  // Monthly subscription (all-access, individual). Default per-month list
  // price (Braavos `list_amount`, which is per-month for a monthly plan).
  monthly?: { priceMinorUnits: number };
  // Bundle subscription (4-month or annual depending on currency). Default
  // per-month list price (Braavos `prepaid_list_amount_per_month`).
  bundle?: { priceMinorUnits: number };
  // One-time standalone program purchase. Default (pre-discount) price
  // (Braavos `original_amount`).
  standalone?: { priceMinorUnits: number };
};

/**
 * Extracts the default per-month monthly + bundle plan shapes (individual
 * tier) from a Braavos `/plan_prices` response. Uses the **list (pre-discount)**
 * per-month prices so the structured data reflects standard pricing with no
 * coupon applied:
 *   - monthly  = `list_amount` (per-month for a monthly plan)
 *   - bundle   = `prepaid_list_amount_per_month` (the list per-month price
 *                for a prepaid 4-month/annual plan)
 *
 * Bundle key is currency-dependent (see `shouldSeeAnnualBundlePlan`):
 * `annually` for AED/AUD/HKD/INR/ZAR, else `4month`.
 */
export function extractPlpPlanPriceData(
  planPrices: BraavosPlanPrices | undefined
): Pick<PlpSeoPriceData, 'monthly' | 'bundle'> {
  if (!planPrices) return {};
  const monthly = planPrices.prices[billingFrequencySchema.enum.monthly]?.individual;
  const bundleKey = shouldSeeAnnualBundlePlan(planPrices.currency)
    ? billingFrequencySchema.enum.annually
    : billingFrequencySchema.enum['4month'];
  const bundle = planPrices.prices[bundleKey]?.individual;
  return {
    monthly: monthly ? { priceMinorUnits: toMonthlyListPrice(monthly) } : undefined,
    bundle: bundle ? { priceMinorUnits: toBundleListPricePerMonth(bundle) } : undefined,
  };
}

// For a monthly plan, `list_amount` IS the per-month list price.
function toMonthlyListPrice(plan: Pick<BraavosPlanPrice, 'priceAmount'>): number {
  return plan.priceAmount;
}

// For a prepaid bundle (4-month/annual), the per-month list price is
// `prepaid_list_amount_per_month` (not the total `list_amount`). Falls back to
// `priceAmount` only if the prepaid field is unexpectedly absent.
function toBundleListPricePerMonth(plan: Pick<BraavosPlanPrice, 'priceAmount' | 'prepaidListAmountPerMonth'>): number {
  return plan.prepaidListAmountPerMonth ?? plan.priceAmount;
}

/**
 * Builds the JSON-LD `Offer` list for a standard ND/course PLP `Product`.
 *
 * Representation choice: **multiple `Offer` entries in `offers`** (not
 * `AggregateOffer`). Udacity is a single seller offering several purchase
 * shapes for the same program (monthly subscription, bundle subscription,
 * one-time purchase). `AggregateOffer` is intended for offers from *different*
 * sellers; for one seller with multiple plan options, an `offers` array of
 * distinct `Offer` nodes is Google's documented and supported pattern. Google
 * renders "From $X" from the lowest-priced offer, which matches the
 * subscription messaging on the PLP.
 *
 * Each offer carries `price`, `priceCurrency`, `availability`, `url`,
 * `itemCondition`, and `priceValidUntil`. Prices are the **default (no-coupon)**
 * list prices — no strikethrough/discount specs are emitted, so the structured
 * data reflects standard program pricing.
 */
export function buildPlpOffers(priceData: PlpSeoPriceData, productUrl: string, now: Date = new Date()): Offer[] {
  const priceValidUntil = computePriceValidUntil(now);
  const { currency, monthly, bundle, standalone } = priceData;
  const offers: Offer[] = [];

  if (monthly) {
    offers.push(
      buildBaseOffer({
        priceMinorUnits: monthly.priceMinorUnits,
        currency,
        url: productUrl,
        priceValidUntil,
        name: 'Monthly Subscription',
      })
    );
  }

  if (bundle) {
    offers.push(
      buildBaseOffer({
        priceMinorUnits: bundle.priceMinorUnits,
        currency,
        url: productUrl,
        priceValidUntil,
        name: shouldSeeAnnualBundlePlan(currency) ? 'Annual Subscription' : '4-Month Bundle',
      })
    );
  }

  if (standalone) {
    offers.push(
      buildBaseOffer({
        priceMinorUnits: standalone.priceMinorUnits,
        currency,
        url: productUrl,
        priceValidUntil,
        name: 'One-Time Purchase',
      })
    );
  }

  return offers;
}

/**
 * Builds a single free `Offer` (`price: "0"`) for free programs/courses.
 */
export function buildFreeOffer(
  productUrl: string,
  currency: Currency = SEO_DEFAULT_CURRENCY,
  now: Date = new Date()
): Offer {
  return buildBaseOffer({
    priceMinorUnits: 0,
    currency,
    url: productUrl,
    priceValidUntil: computePriceValidUntil(now),
    name: 'Free',
  });
}

/**
 * Extracts the standalone (one-time) program price shape from a Braavos
 * `/prices?type=EnrollmentSku` response. Uses the **default (pre-discount)**
 * `original_amount` so the structured data reflects standard pricing with no
 * coupon applied. Returns `undefined` when Braavos had no one-time purchase
 * option for the program (the PLP then renders only the subscription offers).
 */
export function extractStandalonePriceData(
  standalone: BraavosProgramPriceResponse | undefined
): { priceMinorUnits: number } | undefined {
  if (!standalone) return undefined;
  if (!(standalone.originalAmount >= 0)) return undefined;
  return {
    priceMinorUnits: standalone.originalAmount,
  };
}

/**
 * Shape of the price data for subscription plan listing pages (`/plans`,
 * `/experience`, `/ai`). Same default (no-coupon) list-price semantics as
 * `PlpSeoPriceData`, but these pages render the **Udacity subscription**
 * tiers (monthly, bundle, team) rather than a specific program, so there is
 * no standalone shape and a team shape is added.
 */
export type SubscriptionSeoPriceData = {
  currency: Currency;
  // Monthly subscription (all-access, individual). Default per-month list
  // price (Braavos `list_amount`).
  monthly?: { priceMinorUnits: number };
  // Bundle subscription (4-month or annual depending on currency). Default
  // per-month list price (Braavos `prepaid_list_amount_per_month`).
  bundle?: { priceMinorUnits: number };
  // Team subscription (monthly, per-user). Default per-user per-month list
  // price (Braavos `list_amount` for the team monthly plan).
  team?: { priceMinorUnits: number };
};

/**
 * Extracts the default monthly + bundle (individual) and team (monthly)
 * plan shapes from a Braavos `/plan_prices` response, for subscription
 * listing pages. Uses the same list (pre-discount) prices as
 * `extractPlpPlanPriceData`, plus the team monthly plan (`prices.monthly.team`).
 */
export function extractSubscriptionPlanPriceData(
  planPrices: BraavosPlanPrices | undefined
): Pick<SubscriptionSeoPriceData, 'monthly' | 'bundle' | 'team'> {
  if (!planPrices) return {};
  const individual = extractPlpPlanPriceData(planPrices);
  const team = planPrices.prices[billingFrequencySchema.enum.monthly]?.team;
  return {
    monthly: individual.monthly,
    bundle: individual.bundle,
    team: team ? { priceMinorUnits: toMonthlyListPrice(team) } : undefined,
  };
}

/**
 * Builds the JSON-LD `Offer` list for a subscription listing page `Product`
 * (the "Udacity Subscription"). Same multiple-`Offer` representation as
 * `buildPlpOffers`, but for the subscription tiers (monthly, bundle, team)
 * rather than a specific program. Prices are the default (no-coupon) list
 * prices.
 */
export function buildSubscriptionOffers(
  priceData: SubscriptionSeoPriceData,
  productUrl: string,
  now: Date = new Date(),
  options?: { includeTeam?: boolean }
): Offer[] {
  const priceValidUntil = computePriceValidUntil(now);
  const { currency, monthly, bundle, team } = priceData;
  const offers: Offer[] = [];

  if (monthly) {
    offers.push(
      buildBaseOffer({
        priceMinorUnits: monthly.priceMinorUnits,
        currency,
        url: productUrl,
        priceValidUntil,
        name: 'Monthly Subscription',
      })
    );
  }

  if (bundle) {
    offers.push(
      buildBaseOffer({
        priceMinorUnits: bundle.priceMinorUnits,
        currency,
        url: productUrl,
        priceValidUntil,
        name: shouldSeeAnnualBundlePlan(currency) ? 'Annual Subscription' : '4-Month Bundle',
      })
    );
  }

  if (team && options?.includeTeam) {
    offers.push(
      buildBaseOffer({
        priceMinorUnits: team.priceMinorUnits,
        currency,
        url: productUrl,
        priceValidUntil,
        name: 'Team Subscription',
      })
    );
  }

  return offers;
}

/**
 * Escapes `<` as `\u003c` in stringified JSON-LD output so the inline `<script>`
 * payload cannot prematurely terminate inside an HTML document. Call this on
 * the `JSON.stringify` result before injecting via `dangerouslySetInnerHTML`.
 */
export function escapeJsonLd(json: string): string {
  return json.replace(/</g, '\\u003c');
}

// Re-exported so callers can log a consistent module path alongside errors.
export const SEO_PRICE_HELPERS_PATH = PATH_TO_MODULE;

export type { BillingFrequency };
